import { Component, Input, computed, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  STOREFRONT_CONTENT_PLACEMENT_KEYS,
  STOREFRONT_SECTION_PLACEMENT_KEYS,
  StorefrontPlacementScope
} from '../../features/catalog/marketplace-storefront-selectors.model';

interface PlacementOption {
  value: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-storefront-placement-key-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StorefrontPlacementKeySelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="value"
      [placeholder]="t(placeholderKey)"
      [showClear]="showClear"
      [disabled]="disabled"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`.selector { width: 100%; }`]
})
export class StorefrontPlacementKeySelectorComponent implements ControlValueAccessor {
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() scope: StorefrontPlacementScope = 'section';
  @Input() placeholderKey = 'catalog.selectors.placementKey.placeholder';
  @Input() showClear = false;
  @Input() appendTo: 'body' | 'self' = 'body';

  readonly value = signal<string | null>(null);
  disabled = false;

  readonly options = computed<PlacementOption[]>(() => {
    const keys = this.scope === 'content'
      ? STOREFRONT_CONTENT_PLACEMENT_KEYS
      : STOREFRONT_SECTION_PLACEMENT_KEYS;
    return keys.map(key => ({
      value: key,
      label: this.localization.translate(`catalog.selectors.placementKey.${key.replace(/\./g, '_')}`)
    }));
  });

  writeValue(value: string | null): void {
    this.value.set(value?.trim() || null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
